import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { getParticulars, createParticular, updateParticular, deleteParticular } from '../../utils/particularsApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { useConfirm } from '../../components/useConfirm';
import CustomConfirm from '../../components/CustomConfirm';
import { hasPermission } from '../../utils/permissions';

const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgb(33, 41, 90);
    transform: translateY(-1px);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f8f9fa;
`;

const Th = styled.th`
  padding: 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.95rem;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #e9ecef;
  }
`;

const Td = styled.td`
  padding: 20px;
  color: #495057;
  font-size: 0.9rem;
  vertical-align: middle;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgb(26, 35, 90);
  }
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #c82333;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.isDisplay ? '#d4edda' : '#f8d7da'};
  color: ${props => props.isDisplay ? '#155724' : '#721c24'};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #dc3545;
  font-size: 1.1rem;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
  color: #dee2e6;
`;

const EmptyStateText = styled.p`
  font-size: 1.1rem;
  margin: 0 0 20px 0;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  min-width: 400px;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.4rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #495057;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  margin: 0;
  cursor: pointer;
  font-size: 0.9rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 25px;
`;

const SaveButton = styled.button`
  background: rgb(31, 41, 107);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background:rgb(3, 45, 90) 35, 90);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a6268;
  }
`;

const Particulars = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreateProfile = hasPermission(user, 'profile_create');
  const canUpdateProfile = hasPermission(user, 'profile_update');
  const canDeleteProfile = hasPermission(user, 'profile_delete');

  const [particulars, setParticulars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingParticular, setEditingParticular] = useState(null);
  const [formData, setFormData] = useState({
    particularName: '',
    isDisplayForInstructor: false
  });
  const [saving, setSaving] = useState(false);

  const { showDeleteConfirm, confirmState } = useConfirm();

  // Fetch particulars on component mount
  useEffect(() => {
    fetchParticulars();
  }, []);

  const fetchParticulars = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getParticulars();
      if (response.success) {
        setParticulars(response.particulars);
      } else {
        setError('Failed to fetch particulars');
      }
    } catch (error) {
      console.error('Error fetching particulars:', error);
      setError('Error fetching particulars');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingParticular(null);
    setFormData({
      particularName: '',
      isDisplayForInstructor: false
    });
    setShowModal(true);
  };

  const handleEditClick = (particular) => {
    setEditingParticular(particular);
    setFormData({
      particularName: particular.particular_name,
      isDisplayForInstructor: particular.is_display_for_instructor
    });
    setShowModal(true);
  };

  const handleDeleteClick = async (particular) => {
    let confirmed = false;
    
    try {
      // Try the custom confirmation first
      confirmed = await showDeleteConfirm(particular.particular_name, 'particular');
    } catch (error) {
      console.error('Error in showDeleteConfirm:', error);
      // Fallback to browser confirm
      confirmed = window.confirm(`Are you sure you want to delete "${particular.particular_name}"? This action cannot be undone.`);
    }

    if (confirmed) {
      try {
        const response = await deleteParticular(particular.id);
        if (response.success) {
          showSuccess('Particular deleted successfully');
          fetchParticulars(); // Refresh the list
        } else {
          showError(response.message || 'Failed to delete particular');
        }
      } catch (error) {
        console.error('Error deleting particular:', error);
        showError('Error deleting particular');
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingParticular(null);
    setFormData({
      particularName: '',
      isDisplayForInstructor: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.particularName.trim()) {
      showError('Particular name is required');
      return;
    }

    try {
      setSaving(true);
      
      const particularData = {
        particularName: formData.particularName.trim(),
        isDisplayForInstructor: formData.isDisplayForInstructor
      };

      let response;
      if (editingParticular) {
        // Update existing particular
        response = await updateParticular(editingParticular.id, particularData);
      } else {
        // Create new particular
        response = await createParticular(particularData);
      }

      if (response.success) {
        showSuccess(editingParticular ? 'Particular updated successfully' : 'Particular created successfully');
        handleModalClose();
        fetchParticulars(); // Refresh the list
      } else {
        showError(response.message || 'Failed to save particular');
      }
    } catch (error) {
      console.error('Error saving particular:', error);
      showError('Error saving particular');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Particulars</Title>
        </Header>
        <LoadingContainer>
          Loading particulars...
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>Particulars</Title>
        </Header>
        <ErrorContainer>
          <div>
            <div>Error: {error}</div>
            <button onClick={fetchParticulars} style={{ marginTop: '10px', padding: '8px 16px' }}>
              Retry
            </button>
          </div>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Particulars</Title>
        {canCreateProfile && (
          <AddButton onClick={handleAddClick}>
            <FaPlus />
            Add New Particular
          </AddButton>
        )}
      </Header>

      <TableContainer>
        {particulars.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>📋</EmptyStateIcon>
            <EmptyStateText>No particulars found</EmptyStateText>
            {canCreateProfile && (
              <AddButton onClick={handleAddClick}>
                <FaPlus />
                Add Your First Particular
              </AddButton>
            )}
          </EmptyState>
        ) : (
          <Table style={{display: 'inline-table'}}>
            <Thead>
              <Tr>
                <Th>Particular</Th>
                <Th>Is Display for Instructor</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {particulars.map((particular) => (
                <Tr key={particular.id}>
                  <Td style={{ fontWeight: '500' }}>{particular.particular_name}</Td>
                  <Td>
                    <StatusBadge isDisplay={particular.is_display_for_instructor}>
                      {particular.is_display_for_instructor ? (
                        <>
                          <FaCheck size={12} />
                          True
                        </>
                      ) : (
                        <>
                          <FaTimes size={12} />
                          False
                        </>
                      )}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButtons>
                      {canUpdateProfile && (
                        <EditButton onClick={() => handleEditClick(particular)}>
                          <FaEdit size={12} />
                          Edit
                        </EditButton>
                      )}
                      {canDeleteProfile && (
                        <DeleteButton onClick={() => handleDeleteClick(particular)}>
                          <FaTrash size={12} />
                          Delete
                        </DeleteButton>
                      )}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </TableContainer>

      {/* Modal */}
      {showModal && (
        <ModalOverlay onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <ModalHeader>
                <ModalTitle>
                  {editingParticular ? 'Edit Particular' : 'Add New Particular'}
                </ModalTitle>
                <CloseButton type="button" onClick={handleModalClose}>
                  <FaTimes />
                </CloseButton>
              </ModalHeader>

              <FormGroup>
                <Label htmlFor="particularName">Particular Name *</Label>
                <Input
                  type="text"
                  id="particularName"
                  name="particularName"
                  value={formData.particularName}
                  onChange={handleInputChange}
                  placeholder="Enter particular name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="isDisplayForInstructor"
                    name="isDisplayForInstructor"
                    checked={formData.isDisplayForInstructor}
                    onChange={handleInputChange}
                  />
                  <CheckboxLabel htmlFor="isDisplayForInstructor">
                    Is displayed for instructors
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <ModalActions>
                <CancelButton type="button" onClick={handleModalClose}>
                  Cancel
                </CancelButton>
                {(editingParticular ? canUpdateProfile : canCreateProfile) && (
                  <SaveButton type="submit" disabled={saving}>
                    {saving ? 'Saving...' : (editingParticular ? 'Update' : 'Create')}
                  </SaveButton>
                )}
              </ModalActions>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      <CustomConfirm 
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={confirmState.onCancel}
        onClose={confirmState.onCancel || (() => {})}
      />
    </Container>
  );
};

export default Particulars; 